/**
 * Compact platform mark for install chips / headers.
 * Mono brand marks (SVG `currentColor` or black-on-transparent PNG) with a
 * shared brightness filter so every chip reads the same weight in light + dark.
 * Letter monogram fallback when no asset exists.
 */

import { cn } from "@/lib/utils"
import type { PlatformId } from "@/lib/types/platform"
import { getPlatformGuide } from "@/data/platform-guides"

import WordPressLogo from "@/assets/logos/platforms/wordpress.svg"
import WebflowLogo from "@/assets/logos/platforms/webflow.svg"
import FramerLogo from "@/assets/logos/platforms/framer.svg"
import ShopifyLogo from "@/assets/logos/platforms/shopify.svg"
import WixLogo from "@/assets/logos/platforms/wix.svg"
import SquarespaceLogo from "@/assets/logos/platforms/squarespace.svg"
import NextJsLogo from "@/assets/logos/platforms/nextjs.svg"
import BubbleLogo from "@/assets/logos/platforms/bubble.png"
import GhostLogo from "@/assets/logos/platforms/ghost.svg"
import KajabiLogo from "@/assets/logos/platforms/kajabi.svg"
import BeehiivLogo from "@/assets/logos/platforms/beehiiv.png"
import KitLogo from "@/assets/logos/platforms/kit.svg"
import LovableLogo from "@/assets/logos/platforms/lovable.svg"
import PodiaLogo from "@/assets/logos/platforms/podia.svg"
import BoltLogo from "@/assets/logos/bolt-ai-logo.png"

const PLATFORM_IMAGE: Partial<Record<PlatformId, string>> = {
  wordpress: WordPressLogo,
  webflow: WebflowLogo,
  framer: FramerLogo,
  shopify: ShopifyLogo,
  wix: WixLogo,
  squarespace: SquarespaceLogo,
  nextjs: NextJsLogo,
  bubble: BubbleLogo,
  ghost: GhostLogo,
  kajabi: KajabiLogo,
  beehiiv: BeehiivLogo,
  kit: KitLogo,
  lovable: LovableLogo,
  podia: PodiaLogo,
  bolt: BoltLogo,
}

type PlatformGlyphProps = {
  platform: PlatformId
  className?: string
  /** Image/icon pixel size hint (Tailwind size classes still override via className). */
  size?: number
}

export function PlatformGlyph({ platform, className, size = 14 }: PlatformGlyphProps) {
  if (platform === "generic") {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded bg-muted text-[11px] font-medium text-muted-foreground",
          className,
        )}
        style={{ width: size, height: size }}
        aria-hidden
      >
        …
      </span>
    )
  }

  const src = PLATFORM_IMAGE[platform]
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn(
          // Mono: black marks → invert in dark mode for contrast on dark chips.
          "shrink-0 object-contain opacity-80 brightness-0 dark:invert",
          className,
        )}
        style={{ width: size, height: size }}
        aria-hidden
      />
    )
  }

  const name = getPlatformGuide(platform).name
  const letter = name.charAt(0).toUpperCase() || "?"

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded bg-muted/80 border border-border/40 text-[11px] font-medium text-muted-foreground shrink-0",
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden
      title={name}
    >
      {letter}
    </span>
  )
}
